/**
 * TrendPage — standalone 4-week activity trend deep dive.
 *
 * Top: rolling 4-week chart per metric (outreach, meetings, quals, closes).
 * Middle: conversion rates by stage week-over-week.
 * Bottom: Attilius coaching triggers ("Volume fine, qual rate -40% - discovery script coaching").
 *
 * Accessible to AE/BDR (own data via /api/me/trend), Manager (team via
 * /api/team/trend), Executive (roll-up via /api/team/trend/roll-up).
 */
import { useMyTrend, useTeamTrend } from "@/lib/useTrend";
import TrendSparkline from "@/components/TrendSparkline";
import { getSession } from "@/lib/session";
import { hasExecutiveAccess } from "@/lib/rbac";
import OperatorShell from "@/components/operator/OperatorShell";

interface MetricRow {
  key: "outreach" | "meetings" | "quals" | "closes";
  label: string;
  color: string;
}

const METRICS: MetricRow[] = [
  { key: "outreach", label: "Outreach", color: "#0B0B0F" },
  { key: "meetings", label: "First meetings", color: "#0B0B0F" },
  { key: "quals", label: "Qualified meetings", color: "#0B0B0F" },
  { key: "closes", label: "Closes", color: "#DC2626" },
];

function pct(n: number): string {
  if (!isFinite(n)) return "-";
  return `${Math.round(n * 100)}%`;
}

export default function TrendPage() {
  const session = getSession();
  const isManagerial = session?.role === "manager" || session?.role === "executive";
  const my = useMyTrend();
  // W36-B: use hasExecutiveAccess so isDualRole managers (e.g. Bryan) get rollup view
  const team = useTeamTrend(hasExecutiveAccess(session) ? "rollup" : "team");

  const trend = isManagerial && team.team?.team ? team.team.team : my.trend;
  const triggers: string[] = trend?.triggers ?? [];

  // F-04 (2026-05-16): wrap orphaned route in OperatorShell so users have nav back home.
  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Activity Trend" }]}
      pageTitle="Activity Trend"
      pageMeta={`4-week rolling view ${isManagerial ? "· Team" : "· Personal"}`}
    >
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-5">
          <h1 className="text-xl font-semibold" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
            Activity trend
          </h1>
          <p className="text-xs text-neutral-600">Rolling 4-week view {isManagerial ? "(team)" : "(personal)"}.</p>
        </div>

        {/* Per-metric rolling chart */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {METRICS.map((m) => {
            const values: number[] = (trend?.weeks ?? []).map((w: any) => Number(w[m.key] ?? 0));
            const last = values.length ? values[values.length - 1] : 0;
            const prev = values.length > 1 ? values[values.length - 2] : 0;
            // F-09 (2026-05-16): when both periods are zero, render neutral "—"
            // not a green +0% (which falsely implies on-target performance).
            const bothZero = prev === 0 && last === 0;
            const delta = prev > 0 ? (last - prev) / prev : 0;
            return (
              <div key={m.key} className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-medium text-neutral-700">{m.label}</span>
                  <span className={`text-xs ${bothZero ? "text-neutral-500" : delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {bothZero ? "—" : (delta >= 0 ? "+" : "") + Math.round(delta * 100) + "%"}
                  </span>
                </div>
                <div className="mt-1 text-2xl font-semibold" style={{ color: "#0B0B0F" }}>{last}</div>
                <div className="mt-2">
                  <TrendSparkline values={values} width={220} height={42} stroke={m.color} ariaLabel={`${m.label} 4-week trend`} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Conversion rates */}
        <div className="mt-6 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
            Conversion rates - week over week
          </h3>
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-neutral-500">
              <tr>
                <th className="pb-2">Week</th>
                <th className="pb-2">Outreach to meeting</th>
                <th className="pb-2">Meeting to qual</th>
                <th className="pb-2">Qual to close</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {(trend?.weeks ?? []).map((w: any, i: number) => {
                const c = trend?.conversions?.[i];
                return (
                  <tr key={w.weekStart}>
                    <td className="py-2">{w.weekStart}</td>
                    <td>{pct(c?.outreachToMeeting ?? 0)}</td>
                    <td>{pct(c?.meetingToQual ?? 0)}</td>
                    <td>{pct(c?.qualToClose ?? 0)}</td>
                  </tr>
                );
              })}
              {(trend?.weeks?.length ?? 0) === 0 && (
                <tr><td className="py-3 text-neutral-500" colSpan={4}>No data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Coaching triggers */}
        <div className="mt-6 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
            Attilius coaching triggers
          </h3>
          {triggers.length === 0 ? (
            <p className="text-sm text-neutral-500">No triggers right now. Activity within expected ranges.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {triggers.map((t, i) => (
                <li key={i} className="rounded border border-orange-200 bg-orange-50 px-3 py-2 text-yellow-900">{t}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </OperatorShell>
  );
}
